import * as moment from 'moment';
import { Component, HostBinding, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ThemingService, HeaderModule, LinkModule, NavbarModule, AlertModule, AlertService, AlertType } from '@michelin/theme';

import { environment } from '../../environments/environment';
import { CustomAuthService } from '../shared/services/auth/auth.service';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UserService } from '../shared/services/user/user.service';
import { AppsService } from '../shared/services/apps.service';
import { InstantChatService } from '../shared/services/instant-chat/instant-chat.service';
import {
  MandatoryPasswordDialogComponent,
  MandatoryPasswordDialogMode
} from '../shared/components/mandatory-password-dialog/mandatory-password-dialog.component';

const darkClassName = 'dark-mode';

interface NavItem {
  label: string;
  icon: string;
  route?: string;
  exact?: boolean;
  // Destination not available yet: item is disabled and shows the "Em breve" flag.
  soon?: boolean;
  action?: () => void;
}

@Component({
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  imports: [
    HeaderModule,
    LinkModule,
    RouterLink,
    RouterLinkActive,
    NavbarModule,
    MatIconButton,
    MatIcon,
    MatMenuModule,
    MatTooltip,
    RouterOutlet,
    AlertModule,
    NgFor,
    NgIf
  ]
})
export class LayoutComponent implements OnInit, OnDestroy {
  @HostBinding('class') private className = this.themingService.theme.className;

  readonly navItems: NavItem[] = [
    { label: 'Página inicial', icon: 'home', route: '/', exact: true },
    { label: 'Ajuda e suporte', icon: 'help_outline', action: () => this.appsService.openFaq() },
    { label: 'Fale com a Michelin', icon: 'chat_bubble_outline', route: '/contact-us' }
  ];

  sideNavOpen = false;
  /** Trilho do desktop recolhido logo após um clique, mesmo com o mouse ainda sobre ele; volta ao normal ao sair. */
  railCollapsed = false;

  readonly socialLinks = [
    { label: 'Instagram da Michelin', href: 'https://www.instagram.com/michelinbrasil/' },
    { label: 'Instagram da BFG', href: 'https://www.instagram.com/bfgoodrichbrasil/' },
    { label: 'Instagram da Levorin', href: 'https://www.instagram.com/levorin_pneus/' }
  ];

  year = moment.utc().format('YYYY');
  version = environment.version;
  title = 'portalfrontend';
  darkMode = this.className === darkClassName;
  userName = '';
  private mandatoryPasswordDialogOpen = false;
  private mandatoryPasswordDialogRef?: MatDialogRef<MandatoryPasswordDialogComponent>;

  constructor(
    private readonly themingService: ThemingService,
    private readonly authService: CustomAuthService,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly dialog: MatDialog,
    private readonly appsService: AppsService,
    private readonly instantChat: InstantChatService,
    private readonly alertService: AlertService
  ) {}

  setTheme(): void {
    this.darkMode = !this.darkMode;
    this.className = this.themingService.display(this.darkMode ? darkClassName : '', this.darkMode);
    this.instantChat.setDarkMode(this.darkMode);
  }

  ngOnInit(): void {
    this.userName = this.authService.getName();
    // O layout só existe atrás do authGuard: aqui a sessão já está autenticada (login novo ou token já guardado).
    this.instantChat.start();
    this.instantChat.setDarkMode(this.darkMode);
    this.checkMandatoryPasswordReset();
  }

  ngOnDestroy(): void {
    this.instantChat.stop();
    // O modal é bloqueante (disableClose, sem closeOnNavigation): sem fechar aqui ele sobrevive ao logout e cobre o /login.
    this.mandatoryPasswordDialogRef?.close();
  }

  openChat(): void {
    // O widget do fornecedor não é removido no logout; sem sessão válida o portal não o abre.
    if (!this.authService.isAuthenticated()) {
      this.instantChat.stop();
      this.authService.expireSession();
      return;
    }
    const result = this.instantChat.open();
    if (result === 'opened') return;
    if (result === 'loading') {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: 'Chat carregando',
        content: 'O chat de atendimento ainda está carregando. Tente novamente em instantes.',
        timestamp: Date.now()
      });
      return;
    }
    this.instantChat.retry();
    this.alertService.showAlert({
      type: AlertType.strong,
      title: 'Chat indisponível',
      content: 'Não foi possível carregar o chat de atendimento. Uma nova tentativa foi iniciada; tente novamente em instantes.',
      timestamp: Date.now()
    });
  }

  get chatButtonLabel(): string {
    switch (this.instantChat.status()) {
      case 'loading':
        return 'Chat de atendimento carregando';
      case 'error':
        return 'Chat de atendimento indisponível, tentar carregar novamente';
      default:
        return 'Abrir chat de atendimento';
    }
  }

  get chatLoading(): boolean {
    return this.instantChat.status() === 'loading';
  }

  toggleSideNav(): void {
    this.sideNavOpen = !this.sideNavOpen;
  }

  @HostListener('document:keydown.escape')
  closeSideNav(): void {
    this.sideNavOpen = false;
  }

  runNavAction(item: NavItem, event?: MouseEvent): void {
    this.closeSideNav();
    this.collapseRail(event);
    item.action?.();
  }

  onNavClick(event: MouseEvent): void {
    this.closeSideNav();
    this.collapseRail(event);
  }

  /** Só cliques de mouse: o foco fica no item e manteria o trilho aberto (:focus-within). Teclado mantém o foco. */
  private collapseRail(event?: MouseEvent): void {
    if (!event || event.detail === 0) return;
    this.railCollapsed = true;
    (event.currentTarget as HTMLElement | null)?.blur();
  }

  private checkMandatoryPasswordReset(): void {
    this.userService.getMe().subscribe({
      next: (me) => {
        if (me.reset_password === true) {
          this.openMandatoryPasswordDialog('reset');
        }
      },
      error: (error: HttpErrorResponse) => {
        // Sessão vencida/negada: o interceptor já encaminha para /login; "verificação" só cabe a falha transitória.
        if (error?.status === 401 || error?.status === 403 || !this.authService.isAuthenticated()) {
          return;
        }
        this.openMandatoryPasswordDialog('verification-error');
      }
    });
  }

  private openMandatoryPasswordDialog(mode: MandatoryPasswordDialogMode): void {
    if (this.mandatoryPasswordDialogOpen) return;
    this.mandatoryPasswordDialogOpen = true;

    this.mandatoryPasswordDialogRef = this.dialog.open(MandatoryPasswordDialogComponent, {
      width: '480px',
      maxWidth: 'calc(100vw - 32px)',
      disableClose: true,
      closeOnNavigation: false,
      restoreFocus: false,
      panelClass: 'mandatory-password-panel',
      data: { mode }
    });
    this.mandatoryPasswordDialogRef.afterClosed().subscribe(() => {
      this.mandatoryPasswordDialogOpen = false;
    });
  }

  get userInitials(): string {
    const parts = this.userName.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    const first = parts[0][0];
    // Nome composto: inicial do primeiro e do último nome.
    return (parts.length > 1 ? first + parts[parts.length - 1][0] : first).toUpperCase();
  }

  goToProfile(): void {
    this.router.navigate(['/settings']);
  }

  logout(): void {
    this.authService.logout();
  }
}
